import * as React from 'react';
import { getAdminApiBaseUrl } from '../utils/adminApiBaseUrl';
import { useState, useEffect } from 'react';
import { 
    Card,
    CardHeader,
    CardContent,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    TextField,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Typography,
    CircularProgress
} from '@mui/material';
import { format, parseISO, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import useAuthorize from '../hooks/useAuthorize';
import { DASHBOARD_MODULE_ROLES } from '../roles/portalRoles';
import { AnalyticsFreshnessNote } from '../utils/AnalyticsFreshnessNote';
import axios from 'axios';

interface PageViewStat {
    page_path: string;
    page_label?: string;
    category_name?: string | null;
    visit_count: number;
    first_visit: string;
    last_visit: string;
}

interface PageViewsStatsResponse {
    data: PageViewStat[];
    summaries?: TopLinkSummaries;
    total: number;
    limit: number;
    offset: number;
    data_through?: string;
    last_updated_at?: string | null;
}

interface TopLinkItem {
    label: string;
    count: number;
}

interface TopLinkSummaries {
    top_products: TopLinkItem[];
    top_pages: TopLinkItem[];
    top_categories: TopLinkItem[];
}

// Helper function to decode URL-encoded strings for display
const decodeUrlForDisplay = (url: string): string => {
    if (!url) return url;
    try {
        // Decode URL-encoded characters (e.g., %20 -> space, %2F -> /)
        return decodeURIComponent(url);
    } catch (error) {
        // If decoding fails, return original string
        return url;
    }
};

const emptySummaries: TopLinkSummaries = {
    top_products: [],
    top_pages: [],
    top_categories: [],
};

const TopListCard = ({
    title,
    subtitle,
    items,
    countLabel,
}: {
    title: string;
    subtitle: string;
    items: TopLinkItem[];
    countLabel: string;
}) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
        <CardHeader title={title} subheader={subtitle} />
        <CardContent sx={{ pt: 0 }}>
            {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No data available
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow key={`${title}-${item.label}-${index}`}>
                                    <TableCell sx={{ width: 36, color: 'text.secondary' }}>
                                        {index + 1}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {decodeUrlForDisplay(item.label)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight="bold">
                                            {Number(item.count || 0).toLocaleString()}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {countLabel}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </CardContent>
    </Card>
);

const DashboardLinks = () => {
    useAuthorize(DASHBOARD_MODULE_ROLES);
    
    // Default date range: first day of the current year through today.
    const getDefaultDateRange = () => {
        const now = new Date();
        const startDate = startOfYear(now);
        
        return {
            fromDate: format(startDate, 'yyyy-MM-dd'),
            toDate: format(now, 'yyyy-MM-dd')
        };
    };
    
    const defaultDates = getDefaultDateRange();
    
    const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly'>('monthly');
    const [fromDate, setFromDate] = useState<string>(defaultDates.fromDate);
    const [toDate, setToDate] = useState<string>(defaultDates.toDate);
    const [quickRange, setQuickRange] = useState<string>('');
    const [pageViewsData, setPageViewsData] = useState<PageViewStat[]>([]);
    const [summaries, setSummaries] = useState<TopLinkSummaries>(emptySummaries);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [dataThrough, setDataThrough] = useState<string | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

    useEffect(() => {
        const fetchPageViewsData = async () => {
            setLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/page-views-stats`;
            const params = {
                from_date: fromDate,
                to_date: toDate,
                limit: rowsPerPage,
                offset: page * rowsPerPage
            };
            
            console.log('🔍 Fetching page views stats with params:', {
                apiUrl,
                params
            });
            
            try {
                const response = await axios.get<PageViewsStatsResponse>(apiUrl, { params });
                console.log('✅ Page Views Stats API Response Status:', response.status);
                console.log('✅ Page views stats data:', response.data);
                
                if (response.data && response.data.data && Array.isArray(response.data.data)) {
                    setPageViewsData(response.data.data);
                    setSummaries(response.data.summaries || emptySummaries);
                    setTotal(response.data.total || 0);
                    setDataThrough(response.data.data_through || response.headers?.['x-analytics-data-through'] || null);
                    setLastUpdatedAt(response.data.last_updated_at || response.headers?.['x-analytics-last-updated'] || null);
                } else {
                    console.warn('⚠️ Response data is not in expected format:', response.data);
                    setPageViewsData([]);
                    setSummaries(emptySummaries);
                    setTotal(0);
                    setDataThrough(null);
                    setLastUpdatedAt(null);
                }
            } catch (error: any) {
                console.error('❌ Error fetching page views stats:', error);
                console.error('❌ Error details:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status
                });
                setPageViewsData([]);
                setSummaries(emptySummaries);
                setTotal(0);
                setDataThrough(null);
                setLastUpdatedAt(null);
            } finally {
                setLoading(false);
            }
        };

        fetchPageViewsData();
    }, [fromDate, toDate, page, rowsPerPage]);

    const handlePeriodChange = (event: any) => {
        setPeriod(event.target.value);
    };

    const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFromDate(event.target.value);
        setQuickRange(''); // Reset quick range when manually changing dates
        setPage(0); // Reset to first page when filters change
    };

    const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setToDate(event.target.value);
        setQuickRange(''); // Reset quick range when manually changing dates
        setPage(0); // Reset to first page when filters change
    };

    const handleQuickRangeChange = (event: any) => {
        const range = event.target.value;
        setQuickRange(range);
        
        if (!range) {
            return; // Don't update dates if "Custom" is selected
        }

        const now = new Date();
        let from = now;
        let to = now;

        switch (range) {
            case 'today':
                from = to = now;
                break;
            case 'yesterday':
                from = to = subDays(now, 1);
                break;
            case 'last7':
                from = subDays(now, 6);
                to = now;
                break;
            case 'thisWeek':
                from = startOfWeek(now, { weekStartsOn: 1 }); // Monday start
                to = endOfWeek(now, { weekStartsOn: 1 });
                break;
            case 'thisMonth':
                from = startOfMonth(now);
                to = endOfMonth(now);
                break;
            default:
                break;
        }

        setFromDate(format(from, 'yyyy-MM-dd'));
        setToDate(format(to, 'yyyy-MM-dd'));
        setPage(0); // Reset to first page when filters change
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatDate = (dateString: string) => {
        try {
            const date = parseISO(dateString);
            return format(date, 'MMM dd, yyyy HH:mm');
        } catch (e) {
            return dateString;
        }
    };
    
    return (
        <Box sx={{ padding: 2 }}>
            <Card>
                <CardHeader
                    title="Page Views Statistics"
                    subheader="Most frequently visited pages in the selected date range"
                />
                <CardContent>
                    <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="To Date"
                                type="date"
                                value={toDate}
                                onChange={handleToDateChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Period</InputLabel>
                                <Select
                                    value={period}
                                    onChange={handlePeriodChange}
                                    label="Period"
                                >
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                    <MenuItem value="quarterly">Quarterly</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Quick Range</InputLabel>
                                <Select value={quickRange} onChange={handleQuickRangeChange} label="Quick Range">
                                    <MenuItem value="">Custom</MenuItem>
                                    <MenuItem value="today">Today</MenuItem>
                                    <MenuItem value="yesterday">Yesterday</MenuItem>
                                    <MenuItem value="last7">Last 7 Days</MenuItem>
                                    <MenuItem value="thisWeek">This Week</MenuItem>
                                    <MenuItem value="thisMonth">This Month</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {dataThrough && (
                        <AnalyticsFreshnessNote dataThrough={dataThrough} lastUpdatedAt={lastUpdatedAt} />
                    )}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : pageViewsData.length === 0 ? (
                        <Box sx={{ padding: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No page views data available for the selected date range
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                                <Grid item xs={12} md={4}>
                                    <TopListCard
                                        title="Top 10 Products"
                                        subtitle="Most product-related views, wishlist actions and inquiries."
                                        items={summaries.top_products}
                                        countLabel="events"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TopListCard
                                        title="Top 10 Pages"
                                        subtitle="Most visited pages, with category and news IDs resolved."
                                        items={summaries.top_pages}
                                        countLabel="visits"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TopListCard
                                        title="Top 10 Categories"
                                        subtitle="Most visited product categories from category links."
                                        items={summaries.top_categories}
                                        countLabel="visits"
                                    />
                                </Grid>
                            </Grid>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Rank</strong></TableCell>
                                            <TableCell><strong>Page Path</strong></TableCell>
                                            <TableCell align="right"><strong>Visit Count</strong></TableCell>
                                            <TableCell><strong>First Visit</strong></TableCell>
                                            <TableCell><strong>Last Visit</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pageViewsData.map((row, index) => (
                                            <TableRow key={row.page_path} hover>
                                                <TableCell>
                                                    {page * rowsPerPage + index + 1}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            fontFamily: 'monospace',
                                                            wordBreak: 'break-all'
                                                        }}
                                                    >
                                                        {decodeUrlForDisplay(row.page_label || row.page_path) || '(empty)'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {row.visit_count.toLocaleString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatDate(row.first_visit)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatDate(row.last_visit)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default DashboardLinks;
